import { OnInit, Optional, Directive } from '@angular/core';
import { Subscription, Observable, Subject } from "rxjs";
import { tap, mergeMap, finalize } from "rxjs/operators";
import esri = __esri;

import { BaseControlComponent } from "../base-control/base-control.component";
import { Button } from "../../../flow/models/flow.model";
import { FlowButtonOverrideService } from "../../../flow/services/flow-button-override.service";
import { Renderer } from "../../../gis/components/odnr-map/renderer/renderer.interface";
import { DocumentManagementService, IAzureBlobSasToken, IUploadFile } from "../../../common/services/document-management.service";
import { StateType } from "../../../common/models/flow.dto";
import { genId } from "../../../core/services/genId";
import { AccessPlan } from "../../../gis/models/access-plan.model";
import { ContextInteractionService } from '../../services/context-interaction.service';


//Contains the strcutures necessary to extract an image of the map and save it for eDiscovery purposes
@Directive()
export class BaseMapControlComponent extends BaseControlComponent implements OnInit {

    protected _currentLatitude: number = 40.059260; //Latitude of ODNR HQ
    protected _currentLongitude: number = -82.965020; //Longitude of ODNQ HQ
    protected _currentAccessPlan: AccessPlan = new AccessPlan([], true, []);

    get currentAccessPlan(): AccessPlan {
        return this._currentAccessPlan;
    }

    set currentAccessPlan(plan: AccessPlan) {
        this._currentAccessPlan = plan
    }

    get currentLatitude(): number {
        return this._currentLatitude;
    }

    set currentLatitude(value: number) {
        if (isNaN(value)) {
            return;
        }
        this._currentLatitude = value;
    }

    get currentLongitude(): number {
        return this._currentLongitude;
    }

    set currentLongitude(value: number) {
        if (isNaN(value)) {
            return;
        }
        this._currentLongitude = value;
    }

    enableExport: boolean = true; //set to false while the remainder of eDiscovery is being built to save on unused images cluttering docmgmt
    exportSubscription = new Subscription();

    mapRendered: boolean = false;
    mapRenderer: Renderer<esri.SceneView | esri.MapView>;
    storedAccessPlan: AccessPlan = new AccessPlan([], true, []);
    storedLatitude: number;
    storedLongitude: number;

    constructor(@Optional() protected readonly buttonOverride: FlowButtonOverrideService, @Optional() protected readonly _docService: DocumentManagementService, public readonly contextService: ContextInteractionService) {
        super(contextService);
    }

    ngOnInit() {
        super.ngOnInit();
        if (!this.isEdit) {
            if (this.buttonOverride) {
                this.buttonOverride.incrementFunction = this._incrementFunction.bind(this);
                this.buttonOverride.decrementFunction = this._decrementFunction.bind(this);
                this.buttonOverride.setValidatorNextButton((button: Button) => { return this.disableNext(button); });
            }
        }        
    }

    buttonDisabled(button: Button): boolean {
        if (typeof button.enabled === "function" && !button.enabled()) return true;
        if (button.isTransition) return (this.flowComponent.flowForm) ? !this.flowComponent.flowForm.valid : false;
        if (this.flowComponent.ngForm.disabled && !button.isSpecial && this.flowComponent.state.stateType === StateType.ReviewForm) {
            return false;
        }
        else {
            return (this.flowComponent.ngForm) ? this.flowComponent.ngForm.invalid : false;
        }            
    }

    disableNext(button: Button): boolean {
        return (this.buttonDisabled(button) || this.flowComponent.disableNavigation || !this.mapRendered);
    }



    protected get doExport(): boolean {
        const value = this.getContext("mapImage");
        const newLatitude = this.storedLatitude !== this.currentLatitude;
        const newLongitude = this.storedLongitude !== this.currentLongitude;
        const newAccessPlan = (this.currentAccessPlan && this.storedAccessPlan && this.currentAccessPlan.ingressPath !== this.storedAccessPlan.ingressPath);
        return this.enableExport && this.mapRendered && ((value === undefined || value === null || value === "") || newLatitude || newLongitude || newAccessPlan);
    }

    onMapReady(data: Renderer<esri.SceneView | esri.MapView>): void {
        this.mapRendered = true;
        this.mapRenderer = data;
    }

    protected _incrementFunction(button: Button): void {
        if (this.mapRenderer && this._docService && this.doExport) {
            let export$ = this.captureImage().pipe(                
                finalize(() => { this.exportSubscription.unsubscribe(); })).subscribe(() => {
                    this.buttonOverride.callOriginalFunction(button);
            }, (error) => {
                console.error(error);
                this.buttonOverride.callOriginalFunction(button);
            }, () => {
                this.buttonOverride.callOriginalFunction(button);
            });
            this.exportSubscription.add(export$);
        } else {
            this.buttonOverride.callOriginalFunction(button);
        }
    }

    private _decrementFunction(button: Button): void {
        this.buttonOverride.callOriginalFunction(button);
    }

    protected captureImage(): Observable<any> {
        let exportObs = this.mapRenderer.export().pipe(
            mergeMap((data) => {
                const file = this.dataUrlToFile(`${genId()}.jpeg`, data.dataUrl);
                return this.uploadFile(file).pipe(tap((saveResult) => {
                    this.storeContext(saveResult.blobUri, "mapImage");
                }));
            }));
        return exportObs;
    }

    private dataUrlToFile(name: string, url: string): IUploadFile {
        var data = url.split(','), mime = data[0].match(/:(.*?);/)[1], bstr = atob(data[1]), n = bstr.length, u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        const fileName = name.replace(" ", "_");
        let blobFile: File;
		blobFile = new File([u8arr], fileName, { type: mime });
        return { fileName: `${fileName}`, rawFile: blobFile } as IUploadFile;
    }

    private blobToFile(blob: Blob, fileName: string): File {
        const b: any = blob;
        b.lastModifiedDate = new Date();
        b.name = fileName;
        return b as File;
    }

    private uploadFile(file: IUploadFile): Observable<IAzureBlobSasToken> {
        const uploadComplete = new Subject<IAzureBlobSasToken>();
        this._docService.provision(file.rawFile.name).subscribe(x => {
            this._docService.processProvisionedFile(file, x, uploadComplete);
        }, (error) => {
            console.error(error);
        });
        return uploadComplete.asObservable()
    }
}
